<script setup lang="ts">
import merge from 'lodash-es/merge';

import type { ListItemModel as LabelModel } from '#layers/prawopl/app/pages/cms/modules/wk-labels/_runtime/services/LabelsService';
import LabelsService from '#layers/prawopl/app/pages/cms/modules/wk-labels/_runtime/services/LabelsService';
import type { ListItemModel as SectionListItemModel } from '#layers/prawopl/app/pages/cms/modules/wk-sections/_runtime/services/SectionsService';
import SectionsService from '#layers/prawopl/app/pages/cms/modules/wk-sections/_runtime/services/SectionsService';

defineOptions({
    name: 'WkLabelArticlesSettings'
});

const emit = defineEmits<{
    (e: 'update:modelValue',): void;
}>();

const props = defineProps<{
    modelValue: any;
}>();

const defaults = ref<any>({
    limit: null,
    labelId: null,
    sectionId: null,
    showAuthor: false,
    showIntroduction: false,
    showPublicationDate: false,
    showArticleImage: false,
    headerLink: ''
});

const form = computed<any>(() =>
{
    return props.modelValue;
});

watch(() => form.value.settings, () =>
{
    triggerModel();
},
{ deep: true });

function triggerModel(): void
{
    emit('update:modelValue', form.value);
}

async function validate(): Promise<void>
{
    form.value.validate(schema => ({
        settings: schema.object({
            labelId: schema.number().nullable()
                .required($t('[[[Wybieranie etykiety jest wymagane]]]'))
                .typeError($t('[[[Wybieranie etykiety jest wymagane]]]'))
        })
    }));
}

async function fetchLabel(id: number): Promise<LabelModel>
{
    const response = await LabelsService.fetchAutocomplete(id);

    return response || null;
}

async function searchLabels(query: string, limit: number): Promise<LabelModel[]>
{
    return await LabelsService.getAutocomplete(query, limit);
}

async function fetchSection(id: number): Promise<SectionListItemModel>
{
    const response = await SectionsService.fetchAutocomplete(id);

    return response || null;
}

async function searchSection(query: string, limit: number): Promise<SectionListItemModel[]>
{
    return await SectionsService.getAutocomplete(query, limit);
}
onBeforeMount(async (): Promise<void> =>
{
    form.value.settings = merge(defaults.value, form.value.settings);
});

defineExpose({
    validate
});
</script>

<template>
    <div>
        <IdeoFormGroup :label="$t('[[[Liczba wyświetlanych aktualności]]]')">
            <IdeoFormInput
                v-model="form.settings.limit"
                type="number"
                :default-value="6"
                :placeholder="$t('[[[Wprowadź limit aktualności]]]')"
            />
        </IdeoFormGroup>

        <IdeoFormGroup
            :label="$t('[[[Etykieta]]]')"
            required
        >
            <IdeoAutocomplete
                v-model="form.settings.labelId"
                name="labelId"
                track-by="id"
                :placeholder="$t('[[[Wyszukaj etykietę...]]]')"
                :default-label="$t('[[[Brak etykiety]]]')"
                :fetch="(id: number) => fetchLabel(id)"
                :search="(query: string, limit: number) => searchLabels(query, limit)"
            >
                <template #selected="{ option }">
                    <div class="text-wrap">
                        {{ option.name }}
                    </div>
                </template>
                <template #option="{ option }">
                    <div class="text-wrap">
                        {{ option.name }}
                    </div>
                </template>
            </IdeoAutocomplete>
        </IdeoFormGroup>

        <IdeoFormGroup :label="$t('[[[Sekcja]]]')">
            <IdeoAutocomplete
                v-model="form.settings.sectionId"
                name="sectionId"
                track-by="id"
                :placeholder="$t('[[[Wyszukaj sekcję...]]]')"
                :default-label="$t('[[[Brak sekcji]]]')"
                :fetch="(id: number) => fetchSection(id)"
                :search="(query: string, limit: number) => searchSection(query, limit)"
            >
                <template #selected="{ option }">
                    <div class="text-wrap">
                        {{ option.name }}
                    </div>
                </template>
                <template #option="{ option }">
                    <div class="text-wrap">
                        {{ option.name }}
                    </div>
                </template>
            </IdeoAutocomplete>
        </IdeoFormGroup>

        <IdeoFormGroup>
            <IdeoFormCheckbox v-model="form.settings.showAuthor" name="showAuthor">
                {{ $t('[[[Pokaż autora]]]') }}
            </IdeoFormCheckbox>
        </IdeoFormGroup>

        <IdeoFormGroup>
            <IdeoFormCheckbox v-model="form.settings.showIntroduction" name="showIntroduction">
                {{ $t('[[[Pokaż wprowadzenie]]]') }}
            </IdeoFormCheckbox>
        </IdeoFormGroup>

        <IdeoFormGroup>
            <IdeoFormCheckbox v-model="form.settings.showPublicationDate" name="showPublicationDate">
                {{ $t('[[[Pokaż datę publikacji]]]') }}
            </IdeoFormCheckbox>
        </IdeoFormGroup>

        <IdeoFormGroup>
            <IdeoFormCheckbox v-model="form.settings.showArticleImage" name="showArticleImage">
                {{ $t('[[[Pokaż zdjęcie artykułu]]]') }}
            </IdeoFormCheckbox>
        </IdeoFormGroup>

        <IdeoFormGroup>
            <IdeoFormInput
                v-model="form.settings.headerLink"
                :label="$t('[[[Link do nagłówka]]]')"
                :placeholder="$t('[[[Wprowadź URL]]]')"
            />
        </IdeoFormGroup>
    </div>
</template>
